<script lang="ts">
  import { createEventDispatcher, getContext } from 'svelte'
  import _ from 'lodash'
  import { v4 as uuid } from 'uuid'
  import { changeMembership, createOrder, getOrder } from '../../api'
  import type { Membership } from '../../api'
  import { loadingChange, ordersAwaitRefetch, defaultPaymentMethod } from '../store'
  import { profileContextKey } from '../context'
  import { Buttons } from '../../elements'
  import { isPaymentSupported, poll } from '../../helpers'
  import PaymentInfo from './PaymentInfo.svelte'
  import PaymentEdit from './PaymentEdit.svelte'

  export let membership: Membership
  export let activeMembershipQuery
  export let selectedProduct
  export let immediateChange = false

  let submitting = false
  let paymentError
  let paymentEdit = false

  $: cardInfo =
    $defaultPaymentMethod?.provider === 'MONEYPLATFORM'
      ? {
          fingerprint: $defaultPaymentMethod?.card?.fingerprint,
          token: $defaultPaymentMethod?.moneyPlatformToken,
          provider: $defaultPaymentMethod?.provider,
          externalId: uuid(),
        }
      : {}

  function beginEdit() {
    paymentEdit = true
  }

  async function endEdit() {
    paymentEdit = false
  }

  const userId = getContext(profileContextKey).getUserId()

  const dispatch = createEventDispatcher()

  function checkOrderCompletion(orderId) {
    const orderQuery = getOrder(orderId)
    return new Promise((resolve, reject) =>
      poll(
        async () => {
          const r = await orderQuery.refetch()
          const order = r.data.order.data
          const state = order.state
          const orderPaymentError = _.result(order, 'payment.paymentIntent.last_payment_error')
          if (state === 'COMPLETED') {
            resolve(true)
            return true
          }
          if (state === 'UNRESOLVED') {
            resolve(false)
            return true
          }
          if (state === 'CREATED' && orderPaymentError) {
            reject(orderPaymentError)
            return true
          }
        },
        () => {
          resolve(false)
        }
      )
    )
  }

  function handleSuccess() {
    dispatch('confirm')
    submitting = false
    paymentError = ''
  }

  function handleFailure(message) {
    loadingChange.set(false)
    submitting = false
    paymentError = `Payment Failed${message ? `: ${message}` : ''}`
  }

  async function addMembership() {
    loadingChange.set(true)
    const r = await createOrder(null, userId, selectedProduct.productId, selectedProduct.id, cardInfo)
    const order = r.data.createOrder.data.order
    const completed = await checkOrderCompletion(order.id)
    if (!completed) {
      throw new Error()
    }
    await activeMembershipQuery.startPolling(200)
  }

  async function upgradeMembership() {
    loadingChange.set(true)
    const r = await createOrder(membership.id, userId, selectedProduct.productId, selectedProduct.id, cardInfo)
    const order = r.data.createOrder.data.order
    const completed = await checkOrderCompletion(order.id)
    if (!completed) {
      throw new Error()
    }
    // If the membership was inactive we need to reactivate
    const input = { status: 'active' }
    await changeMembership(userId, input)
    await activeMembershipQuery.startPolling(200)
  }

  async function downgradeMembership() {
    const input = { context: { inventoryId: selectedProduct.id } }
    await changeMembership(userId, input)
    await activeMembershipQuery.refetch()
    ordersAwaitRefetch.set(true)
  }

  async function handleChangeMembership() {
    if (membership) {
      if (selectedProduct.level > membership.level) {
        try {
          await upgradeMembership()
          handleSuccess()
        } catch (e) {
          handleFailure(null)
          console.log('error', e)
        }
      }
      if (selectedProduct.level < membership.level) {
        await downgradeMembership()
        handleSuccess()
      }
    } else {
      try {
        await addMembership()
        handleSuccess()
      } catch (e) {
        console.log('error', e)
        handleFailure(e && e.message)
      }
    }
  }

  async function changeRenewalPlan() {
    const input = { context: { inventoryId: selectedProduct.id } }
    await changeMembership(userId, input)
    await activeMembershipQuery.refetch()
    handleSuccess()
  }

  function handleConfirm() {
    submitting = true
    paymentError = ''

    if (immediateChange) {
      handleChangeMembership()
    } else {
      changeRenewalPlan()
    }
  }
</script>

{#if paymentEdit}
  <PaymentEdit on:finish={endEdit} />
{:else}
  <div class="plan-info">
    <p class="title">selected plan</p>
    <p class="content">{selectedProduct.name} ${(selectedProduct.priceCents.amount / 100).toFixed(2)}/yr</p>
  </div>

  <PaymentInfo error={paymentError} on:edit={beginEdit} />

  {#if immediateChange}
    <div class="membership-info">
      {#if selectedProduct.level < membership?.level}
        <p>
          The current membership {membership.name} will change to {selectedProduct.name} at the end of membership term.
        </p>
      {:else if selectedProduct.level > membership?.level}
        <p>
          The current membership {membership.name} will change to {selectedProduct.name} and trigger a charge.
        </p>
      {:else}
        <p>
          Membership will auto-renew each year using the saved credit card on file. Members can update payment
          information or cancel their plan at any time.
        </p>
        <p>
          If canceled within the first 60 days of purchase, member will receive a full refund. Each day afterwards will
          be prorated.
        </p>
      {/if}
    </div>
  {:else}
    <div class="membership-info">
      <p>
        Membership will auto-renew each year using the saved credit card on file. Members can update payment information
        or cancel their plan at any time.
      </p>
      <p>
        If canceled within the first 60 days of purchase, member will receive a full refund. Each day afterwards will be
        prorated.
      </p>
    </div>
  {/if}

  <Buttons
    confirm={submitting ? 'Submitting...' : 'Confirm'}
    cancel="Back"
    confirmDisabled={!$defaultPaymentMethod || submitting}
    showConfirm={isPaymentSupported($defaultPaymentMethod?.provider)}
    on:confirm={handleConfirm}
    on:cancel
  />
  {#if $defaultPaymentMethod && !isPaymentSupported($defaultPaymentMethod?.provider)}
    <p class="error">Payment provider not supported: {$defaultPaymentMethod?.provider}</p>
  {/if}
{/if}

<style>
  .plan-info {
    background: #f4f6f8;
    padding: 8px 12px;
  }

  .plan-info .title {
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
  }

  .membership-info {
    margin-top: 24px;
  }

  .membership-info p {
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 24px;
    color: #484c51;
  }

  .error {
    color: #ff0000;
  }
</style>
